import React, {useState, useEffect} from "react";
import {getAdminListController, ModifyAdminDashboard, DeleteUsers, ChangeUserStatus} from "../../services/adminServices";
import moment from "moment";
import CustomLoader from "../../CustomComponents/CustomLoader";
import {Autocomplete, TextField, CircularProgress} from "@mui/material";
import {sweetAlertQuestion, sweetAlerts} from "../../utils/sweet-alerts";

const AdminProfile = () => {
	const [firstTimeLoad, setFirstTimeLoad] = useState(true);
	const [admins, setAdmins] = useState([]);

	const [loading, setLoading] = useState(true);

	const [filterStatus, setFilterStatus] = useState("all");
	const [sortBy, setSortBy] = useState("name-asc");

	const [showDetailModal, setShowDetailModal] = useState(false);
	const [selectedAdmin, setSelectedAdmin] = useState(null);
	const [showEditModal, setShowEditModal] = useState(false);
	const [showAddModal, setShowAddModal] = useState(false);
	const [editingAdmin, setEditingAdmin] = useState(null);
	const [editFormData, setEditFormData] = useState({});
	const [addFormData, setAddFormData] = useState({
		first_name: "",
		last_name: "",
		email: "",
		password: "",
	});
	const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
	const [isSubmittingAdd, setIsSubmittingAdd] = useState(false);
	const [editErrors, setEditErrors] = useState({});
	const [addErrors, setAddErrors] = useState({});
	const [loadingButtons, setLoadingButtons] = useState({});
	const [totalCount, setTotalCount] = useState(0);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [totalPages, setTotalPages] = useState(1);
	const [totalLength, setTotalLength] = useState(0);

	const getMockAdmins = async () => {
		try {
			if (firstTimeLoad) {
				setLoading(false);
			}

			// Use current page and pageSize instead of hardcoded values
			const response = await getAdminListController({
				page: page, 
				pagesize: pageSize, 
				sortby: sortBy, 
				status: filterStatus
			});

			if (response.status) {
				setTotalCount(response.data.totalCount);
				setAdmins(response.data.list);
				setLoading(true);
				setTotalLength(response?.data?.totalCount);
				const totalPages = Math.ceil(response?.data?.totalCount / pageSize);
				setTotalPages(totalPages);
			}
		} catch (error) {
			console.error("Error fetching admins:", error);
			setAdmins([]);
			setLoading(true);
			// setFilteredAdmins([]);
		}
	};

	useEffect(() => {
		getMockAdmins();
	}, [pageSize, page]);

	useEffect(() => {
		if (firstTimeLoad) {
			setFirstTimeLoad(false);
		} else {
			setPage(1);
			getMockAdmins();
		}
	}, [sortBy, filterStatus]);

	const openAddModal = () => {
		setAddFormData({
			first_name: "",
			last_name: "",
			email: "",
			role: "admin",
			status: "active",
		});
		setAddErrors({});
		setShowAddModal(true);
	};
	const closeAddModal = () => {
		setShowAddModal(false);
		setAddFormData({
			first_name: "",
			last_name: "",
			email: "",
			role: "admin",
			status: "active",
		});
		setAddErrors({});
	};

	const handleEditInputChange = (field, value) => {
		setEditFormData((prev) => ({
			...prev,
			[field]: value,
		}));

		if (editErrors[field]) {
			setEditErrors((prev) => ({
				...prev,
				[field]: null,
			}));
		}
	};

	const handleAddInputChange = (field, value) => {
		setAddFormData((prev) => ({
			...prev,
			[field]: value,
		}));

		if (addErrors[field]) {
			setAddErrors((prev) => ({
				...prev,
				[field]: null,
			}));
		}
	};

	const validateAddForm = () => {
		const errors = {};

		if (!addFormData.first_name?.trim()) {
			errors.first_name = "First name is required";
		}

		if (!addFormData.last_name?.trim()) {
			errors.last_name = "Last name is required";
		}

		if (!addFormData.email?.trim()) {
			errors.email = "Email is required";
		} else if (!/\S+@\S+\.\S+/.test(addFormData.email)) {
			errors.email = "Email is invalid";
		}

		if (!addFormData.password?.trim()) {
			errors.password = "Password is required";
		} else if (addFormData.password.length < 6) {
			errors.password = "Password must be at least 6 characters";
		} else if (!/[A-Z]/.test(addFormData.password)) {
			errors.password = "Password must contain at least one uppercase letter";
		} else if (!/[0-9]/.test(addFormData.password)) {
			errors.password = "Password must contain at least one number";
		}

		setAddErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const handleAddSubmit = async () => {
		if (!validateAddForm()) {
			return;
		}

		setIsSubmittingAdd(true);
		try {
			const response = await ModifyAdminDashboard({
				...addFormData,
			});
			if (response.status) {
				sweetAlerts("success", "Admin added successfully!");
				await getMockAdmins();
				closeAddModal();
				setIsSubmittingAdd(false);
			} else {
				setIsSubmittingAdd(false);
				sweetAlerts("error", response?.message || "Error adding admin. Please try again.");
			}
		} catch (error) {
			sweetAlerts("error", "Error adding admin. Please try again.");
			setIsSubmittingAdd(false);
		}
	};

	const handleViewDetails = (admin) => {
		setSelectedAdmin(admin);
		setShowDetailModal(true);
	};

	const truncateWords = (text, maxChars) => {
		if (text.length <= maxChars) return text;
		return text.substring(0, maxChars) + "...";
	};

	const getStatusBadge = (status) => {
		const config = status ? "Active" : "Inactive";
		return <span className={`badge ${status ? "badge-success" : "badge-warning"}`}>{config}</span>;
	};

	const handleToggleStatus = async (adminId) => {
		setLoadingButtons((prev) => ({...prev, [`toggle-${adminId}`]: true}));

		try {
			const response = await ChangeUserStatus({user_id: adminId});
			if (response.status) {
				await getMockAdmins();
				sweetAlerts("success", "Admin status updated successfully");
				setLoadingButtons((prev) => ({...prev, [`toggle-${adminId}`]: false}));
			} else {
				setLoadingButtons((prev) => ({...prev, [`toggle-${adminId}`]: false}));
				sweetAlerts("error", response?.message || "Error updating admin status. Please try again.");
			}
		} catch (error) {
			setLoadingButtons((prev) => ({...prev, [`toggle-${adminId}`]: false}));
			sweetAlerts("error", "Error updating admin status. Please try again.");
		}
	};

	const closeEditModal = () => {
		setShowEditModal(false);
		setEditingAdmin(null);
		setEditFormData({});
		setEditErrors({});
		if (editingAdmin) {
			setLoadingButtons((prev) => ({...prev, [`edit-${editingAdmin.id}`]: false}));
		}
	};

	const openEditModal = (admin) => {
		setEditingAdmin(admin);
		setEditFormData({
			id: admin?.user_id,
			first_name: admin.first_name,
			last_name: admin.last_name,
			email: admin.email,
			role: admin.role,
			status: admin.status,
		});
		setEditErrors({});
		setShowEditModal(true);
	};

	const validateEditForm = () => {
		const errors = {};

		if (!editFormData.first_name?.trim()) {
			errors.first_name = "First name is required";
		}

		if (!editFormData.last_name?.trim()) {
			errors.last_name = "Last name is required";
		}

		if (!editFormData.email?.trim()) {
			errors.email = "Email is required";
		} else if (!/\S+@\S+\.\S+/.test(editFormData.email)) {
			errors.email = "Email is invalid";
		}

		setEditErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const handleEditSubmit = async () => {
		if (!validateEditForm()) {
			return;
		}

		sweetAlertQuestion(`Are you sure you want to update this admin?`).then(async (result) => {
			if (result === "Yes") {
				setIsSubmittingEdit(true);
				setLoadingButtons((prev) => ({...prev, [`edit-${editingAdmin.user_id}`]: true}));

				try {
					const response = await ModifyAdminDashboard({
						first_name: editFormData.first_name,
						last_name: editFormData.last_name,
						email: editingAdmin.email,
						userid: editingAdmin.user_id,
					});
					setLoadingButtons((prev) => ({...prev, [`edit-${editingAdmin.user_id}`]: false}));
					if (response.status) {
						sweetAlerts("success", "Admin updated successfully!");
						await getMockAdmins();
						closeEditModal();
						setIsSubmittingEdit(false);
					} else {
						setIsSubmittingEdit(false);
						sweetAlerts("error", response?.message || "Error updating admin. Please try again.");
					}
				} catch (error) {
					sweetAlerts("error", "Error updating admin. Please try again.");
					setIsSubmittingEdit(false);
				}
			}
		});
	};

	const handleDeleteAdmin = async (adminId) => {
		sweetAlertQuestion("Are you sure you want to deactivate this admin? This action cannot be undone.").then(async (result) => {
			if (result === "Yes") {
				setLoadingButtons((prev) => ({...prev, [`delete-${adminId}`]: true}));
				try {
					const response = await DeleteUsers({user_id: adminId});
					if (response.status) {
						// Update total count first
						const newTotalCount = totalCount - 1;
						setTotalCount(newTotalCount);
						setTotalLength(newTotalCount);
						
						// Calculate new total pages
						const newTotalPages = Math.ceil(newTotalCount / pageSize);
						setTotalPages(newTotalPages);
						
						// Check if current page is now empty and not the first page
						if (page > 1 && page > newTotalPages) {
							// Navigate to the previous page
							const newPage = newTotalPages;
							setPage(newPage);
							// Fetch data for the new page
							await getMockAdmins();
						} else {
							// Just refresh current page data
							await getMockAdmins();
						}
						
						sweetAlerts("success", "Admin deleted successfully");
					} else {
						setLoadingButtons((prev) => ({...prev, [`delete-${adminId}`]: false}));
						sweetAlerts("error", response?.message || "Error deleting admin. Please try again.");
					}
				} catch (error) {
					setLoadingButtons((prev) => ({...prev, [`delete-${adminId}`]: false}));
					sweetAlerts("error", "Error deleting admin. Please try again.");
				}
			}
		});
	};

	const paginate = (pageNumber) => {
		setPage(pageNumber);
	};

	return (
		<div className="admin-ratings-dashboard-page">
			<div className="page-header d-flex justify-content-between">
				<div className="">
					<h2>Admin Management</h2>
					<p>View and manage all admin users ({totalCount} total)</p>
				</div>{" "}
				<div className="">
					<button className="btn btn-primary" onClick={openAddModal}>
						<svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{marginRight: "8px"}}>
							<path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
						</svg>
						Add Admin
					</button>
				</div>
			</div>

			<div className="ratings-controls">
				<div className="filter-section">
					<select className="filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
						<option value="all">All Status</option>
						<option value="active">Active</option>
						<option value="inactive">Inactive</option>
					</select>

					<select className="filter-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
						<option value="created-desc">Newest First</option>
						<option value="created-asc">Oldest First</option>
						<option value="email-asc">Email A-Z</option>
						<option value="email-desc">Email Z-A</option>
					</select>
				</div>
			</div>

			{loading ? (
				<div className="admin-ratings-container">
					{admins.length === 0 ? (
						<div className="ratings-table-container">
							<div className="table-responsive sticky-table">
								<table className="ratings-table">
									<thead>
										<tr>
											<th className="align-middle address-cell">Name</th>
											<th className="align-middle address-cell">Email</th>
											<th className="align-middle address-cell">Status</th>
											<th className="align-middle address-cell">Created</th>
											<th className="actions-cell text-end">Actions</th>
										</tr>
									</thead>
									<tbody>
										<tr className="rating-row">
											<td className="address-cell text-center" colSpan={5}>
												No admins found
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
					) : (
						<div className="ratings-table-container">
							<div className="table-responsive sticky-table">
								<table className="ratings-table">
									<thead>
										<tr>
											<th className="align-middle address-cell">Name</th>
											<th className="align-middle address-cell">Email</th>
											<th className="align-middle address-cell">Status</th>
											<th className="align-middle address-cell">Created</th>
											<th className="actions-cell text-end">Actions</th>
										</tr>
									</thead>
									<tbody>
										{admins.map((admin) => (
											<tr key={admin?.user_id} className="rating-row">
												<td className="align-middle address-cell cursor-pointer" onClick={() => handleViewDetails(admin)}>
													{truncateWords(`${admin.first_name} ${admin.last_name}`, 30)}
												</td>
												<td className="align-middle address-cell cursor-pointer" onClick={() => handleViewDetails(admin)}>
													{truncateWords(admin.email, 30)}
												</td>
												<td className="align-middle address-cell cursor-pointer" onClick={() => handleViewDetails(admin)}>
													{getStatusBadge(admin.status)}
												</td>
												<td className="align-middle address-cell cursor-pointer" onClick={() => handleViewDetails(admin)}>
													{moment(admin.created_at).format("MMM DD, YYYY")}
												</td>
												<td className="align-middle actions-cell">
													<div className="table-actions mb-0 justify-content-end flex-row">
														<button
															className="btn-icon"
															onClick={() => handleToggleStatus(admin?.user_id)}
															title={admin.status === "active" ? "Deactivate" : "Activate"}
															disabled={loadingButtons[`toggle-${admin?.user_id}`]}>
															{loadingButtons[`toggle-${admin?.user_id}`] ? (
																<span className="spinner-small"></span>
															) : (
																<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
																	<path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
																	<path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" stroke="currentColor" strokeWidth="2" />
																</svg>
															)}
														</button>
														<button className="btn-icon" onClick={() => handleViewDetails(admin)} title="View Details">
															<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
																<path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="currentColor" strokeWidth="2" />
																<circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
															</svg>
														</button>
														<button className="btn-icon btn-edit" onClick={() => openEditModal(admin)} title="Edit Admin" disabled={loadingButtons[`edit-${admin?.user_id}`]}>
															{loadingButtons[`edit-${admin?.user_id}`] ? (
																<span className="spinner-small"></span>
															) : (
																<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
																	<path
																		d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13"
																		stroke="currentColor"
																		strokeWidth="2"
																		strokeLinecap="round"
																		strokeLinejoin="round"
																	/>
																	<path
																		d="M18.5 2.50023C18.8978 2.1024 19.4374 1.87891 20 1.87891C20.5626 1.87891 21.1022 2.1024 21.5 2.50023C21.8978 2.89805 22.1213 3.43762 22.1213 4.00023C22.1213 4.56284 21.8978 5.1024 21.5 5.50023L12 15.0002L8 16.0002L9 12.0002L18.5 2.50023Z"
																		stroke="currentColor"
																		strokeWidth="2"
																		strokeLinecap="round"
																		strokeLinejoin="round"
																	/>
																</svg>
															)}
														</button>
														<button className="btn-icon btn-delete" onClick={() => handleDeleteAdmin(admin?.user_id)} title="Delete Admin" disabled={loadingButtons[`delete-${admin?.user_id}`]}>
															{loadingButtons[`delete-${admin?.user_id}`] ? (
																<span className="spinner-small"></span>
															) : (
																<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
																	<path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
																	<path
																		d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z"
																		stroke="currentColor"
																		strokeWidth="2"
																		strokeLinecap="round"
																		strokeLinejoin="round"
																	/>
																</svg>
															)}
														</button>
													</div>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							{/* Pagination */}
							{totalPages > 0 && (
								<>
									<div className="pagination justify-content-between mt-0 pt-0 pe-3 px-3">
										<div className="pagination-info d-flex justify-content-between align-items-center gap-3">
											<span className="pagination-info-text">
												Showing {page * pageSize - pageSize + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount} entries
											</span>
											<div>
												<select className="filter-select" value={pageSize} onChange={(e) => setPageSize(e.target.value)}>
													<option value="10">10</option>
													<option value="20">20</option>
													<option value="25">25</option>
													<option value="50">50</option>
													<option value="100">100</option>
												</select>
											</div>
										</div>
										<div className="d-flex justify-content-end pb-3 pt-3 pe-2">
											<button className="pagination-btn" onClick={() => paginate(page - 1)} disabled={page === 1}>
												Previous
											</button>

											<div className="pagination-numbers">
												{[...Array(totalPages)].map((_, index) => {
													const ipage = index + 1;
													if (ipage === 1 || ipage === totalPages || (ipage >= page - 1 && ipage <= page + 1)) {
														return (
															<button key={ipage} className={`pagination-number ${page === ipage ? "active" : ""}`} onClick={() => paginate(ipage)}>
																{ipage}
															</button>
														);
													} else if (ipage === page - 2 || ipage === page + 2) {
														return (
															<span key={ipage} className="pagination-ellipsis">
																...
															</span>
														);
													}
													return null;
												})}
											</div>

											<button className="pagination-btn" onClick={() => paginate(page + 1)} disabled={page === totalPages}>
												Next
											</button>
										</div>
									</div>
								</>
							)}
						</div>
					)}
				</div>
			) : (
				<div className="admin-ratings-container">
					<div className="ratings-table-container">
						<div className="table-responsive sticky-table">
							<table className="ratings-table">
								<thead>
									<tr>
										<th className="align-middle address-cell">Name</th>
										<th className="align-middle address-cell">Email</th>
										<th className="align-middle address-cell">Status</th>
										<th className="align-middle address-cell">Created</th>
										<th className="actions-cell text-end">Actions</th>
									</tr>
								</thead>
								<tbody>
									<tr className="rating-row">
										<td className="address-cell text-center" colSpan={5}>
											<CustomLoader />
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</div>
			)}

			{showAddModal && (
				<div className="modal-overlay" onClick={closeAddModal}>
					<div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: "600px"}}>
						<div className="modal-header">
							<h3>Add New Admin</h3>
							<button className="modal-close" onClick={closeAddModal}>
								×
							</button>
						</div>

						<div className="modal-body">
							<div className="form-group">
								<label className="form-label">First Name *</label>
								<input
									type="text"
									className={`form-input ${addErrors.first_name ? "error" : ""}`}
									value={addFormData.first_name || ""}
									onChange={(e) => handleAddInputChange("first_name", e.target.value)}
									placeholder="Enter first name"
								/>
								{addErrors.first_name && <div className="error-message">{addErrors.first_name}</div>}
							</div>

							<div className="form-group">
								<label className="form-label">Last Name *</label>
								<input
									type="text"
									className={`form-input ${addErrors.last_name ? "error" : ""}`}
									value={addFormData.last_name || ""}
									onChange={(e) => handleAddInputChange("last_name", e.target.value)}
									placeholder="Enter last name"
								/>
								{addErrors.last_name && <div className="error-message">{addErrors.last_name}</div>}
							</div>

							<div className="form-group">
								<label className="form-label">Email *</label>
								<input
									type="email"
									className={`form-input ${addErrors.email ? "error" : ""}`}
									value={addFormData.email || ""}
									onChange={(e) => handleAddInputChange("email", e.target.value)}
									placeholder="Enter email address"
								/>
								{addErrors.email && <div className="error-message">{addErrors.email}</div>}
							</div>

							<div className="form-group">
								<label className="form-label">Password *</label>
								<input
									type="password"
									className={`form-input ${addErrors.password ? "error" : ""}`}
									value={addFormData.password || ""}
									onChange={(e) => handleAddInputChange("password", e.target.value)}
									placeholder="Enter password"
								/>
								{addErrors.password && <div className="error-message">{addErrors.password}</div>}
							</div>
						</div>

						<div className="modal-footer">
							<button className="btn btn-primary" onClick={handleAddSubmit} disabled={isSubmittingAdd}>
								{isSubmittingAdd ? (
									<>
										<span className="spinner-small"></span>
										Adding...
									</>
								) : (
									"Add Admin"
								)}
							</button>
							<button className="btn btn-secondary" onClick={closeAddModal} disabled={isSubmittingAdd}>
								Cancel
							</button>
						</div>
					</div>
				</div>
			)}

			{showDetailModal && selectedAdmin && (
				<div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
					<div className="modal-content" onClick={(e) => e.stopPropagation()}>
						<div className="modal-header">
							<h3>Admin Details</h3>
							<button className="modal-close" onClick={() => setShowDetailModal(false)}>
								×
							</button>
						</div>

						<div className="modal-body">
							<div className="detail-section">
								<h4>Admin Information</h4>
								<div className="detail-grid">
									<div className="detail-item">
										<span className="detail-label">Name:</span>
										<span className="detail-value">
											{selectedAdmin.first_name} {selectedAdmin.last_name}
										</span>
									</div>
									<div className="detail-item">
										<span className="detail-label">Email:</span>
										<span className="detail-value">{selectedAdmin.email}</span>
									</div>

									<div className="detail-item">
										<span className="detail-label">Status:</span>
										<span className="detail-value">{getStatusBadge(selectedAdmin.status)}</span>
									</div>
									<div className="detail-item">
										<span className="detail-label">Created:</span>
										<span className="detail-value">{moment(selectedAdmin.created_at).format("MMMM DD, YYYY")}</span>
									</div>
								</div>
							</div>
						</div>

						<div className="modal-footer">
							<button
								className="btn btn-primary"
								onClick={() => {
									setShowDetailModal(false);
									openEditModal(selectedAdmin);
								}}>
								Edit Admin
							</button>
							<button className="btn btn-secondary" onClick={() => setShowDetailModal(false)}>
								Close
							</button>
						</div>
					</div>
				</div>
			)}

			{showEditModal && editingAdmin && (
				<div className="modal-overlay" onClick={closeEditModal}>
					<div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: "600px"}}>
						<div className="modal-header">
							<h3>Edit Admin</h3>
							<button className="modal-close" onClick={closeEditModal}>
								×
							</button>
						</div>

						<div className="modal-body">
							<div className="form-group">
								<label className="form-label">First Name *</label>
								<input
									type="text"
									className={`form-input ${editErrors.first_name ? "error" : ""}`}
									value={editFormData.first_name || ""}
									onChange={(e) => handleEditInputChange("first_name", e.target.value)}
									placeholder="Enter first name"
								/>
								{editErrors.first_name && <div className="error-message">{editErrors.first_name}</div>}
							</div>

							<div className="form-group">
								<label className="form-label">Last Name *</label>
								<input
									type="text"
									className={`form-input ${editErrors.last_name ? "error" : ""}`}
									value={editFormData.last_name || ""}
									onChange={(e) => handleEditInputChange("last_name", e.target.value)}
									placeholder="Enter last name"
								/>
								{editErrors.last_name && <div className="error-message">{editErrors.last_name}</div>}
							</div>

							<div className="form-group">
								<label className="form-label">Email *</label>
								<input
									type="email"
									className={`form-input   ${editErrors.email ? "error" : ""}`}
									value={editFormData.email || ""}
									onChange={(e) => handleEditInputChange("email", e.target.value)}
									placeholder="Enter email address"
									disabled
								/>
								{editErrors.email && <div className="error-message">{editErrors.email}</div>}
							</div>
						</div>

						<div className="modal-footer">
							<button className="btn btn-primary" onClick={handleEditSubmit} disabled={isSubmittingEdit}>
								{isSubmittingEdit ? (
									<>
										<span className="spinner-small"></span>
										Updating...
									</>
								) : (
									"Update Admin"
								)}
							</button>
							<button className="btn btn-secondary" onClick={closeEditModal} disabled={isSubmittingEdit}>
								Cancel
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default AdminProfile;
